import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "../../../../utils/helpers/enums/routes";
import { useSelector } from "react-redux";
import useStore from "../../../../hooks/useStore";
import { isAfter } from "date-fns";
import { 
  MdCalendarToday as CalendarTick,
  MdClose as CrossRed,
} from "react-icons/md";
import LearnLocalLogo from "assets/images/others/host-side-images/LL-Full Color-4.png";

// Profile level enum matching Flutter code
enum ProfileLevel {
  approved = 'approved',
  sessionOnGoing = 'sessionOnGoing',
  waitingForApproval = 'waitingForApproval',
  profileCompleted = 'profileCompleted',
  rejected = 'rejected',
  profileUnCompleted = 'profileUnCompleted',
}

interface ProfileLevelConfig {
  title: string;
  message: string;
  color: string;
  imagePath: string | React.ReactNode;
  showButton: boolean;
  buttonText?: string;
  buttonAction?: (e: React.MouseEvent) => void;
}

const ProfileCompletionModal: React.FC = () => {
  const { setProfileModalOpen } = useStore();
  const profileModalOpen = useSelector(
    (state: any) => state.sharedReducer.profileModalOpen
  );
  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  const navigate = useNavigate();

  const onClose = () => {
    setProfileModalOpen(false);
  };

  // Check profile level - matching Flutter logic exactly
  const checkProfileLevel = (): ProfileLevel => {
    const ambassador = registeredData?.profiles?.ambassador;

    if (ambassador == null || !ambassador.profileCompleted) {
      return ProfileLevel.profileUnCompleted;
    }

    if (ambassador.isApproved == null) {
      if (ambassador.bookedSessions != null && 
          ambassador.bookedSessions.startTime) {
        const sessionStartTime = new Date(ambassador.bookedSessions.startTime);
        if (isAfter(sessionStartTime, new Date())) {
          return ProfileLevel.sessionOnGoing;
        }
      }
      return ProfileLevel.waitingForApproval;
    }

    return ambassador.isApproved
      ? ProfileLevel.approved
      : ProfileLevel.rejected;
  };

  // Check if step one (user profile) is complete
  const isStepOneComplete = () => {
    const user = registeredData?.user;
    return !!(
      user?.firstName &&
      user?.lastName &&
      user?.email &&
      user?.language &&
      user?.address &&
      user?.phone &&
      user?.dob &&
      user?.gender &&
      user?.state &&
      user?.city &&
      user?.zipCode
    );
  };

  const goToProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileModalOpen(false);
    // Use setTimeout to ensure the modal closes before navigation
    setTimeout(() => {
      // If step one is complete, navigate to welcome page
      // Otherwise, navigate to profile page to complete step one
      if (isStepOneComplete()) {
        navigate(siteRoutes.ambassadorWelcome);
      } else {
        navigate(siteRoutes.ambassadorProfile);
      }
    }, 100);
  };

  // Get configuration based on profile level
  const getProfileLevelConfig = (profileLevel: ProfileLevel): ProfileLevelConfig => {
    switch (profileLevel) {
      case ProfileLevel.profileUnCompleted:
        return {
          title: "Profile Incomplete",
          message: "Please complete your profile to continue",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: true,
          buttonText: "Continue",
          buttonAction: goToProfile,
        };
      
      case ProfileLevel.waitingForApproval:
        return {
          title: "Thank you for Attending Live Session",
          message: "You have successfully taken Live Session for Ambassador Profile. Please wait for confirmation from Admin to unlock further features",
          color: "text-primaryBlueText",
          imagePath: <CalendarTick className="w-32 h-32 text-primaryBlueText mx-auto" />,
          showButton: false,
        };
      
      case ProfileLevel.sessionOnGoing:
        return {
          title: "Take a Live Session First!",
          message: "You currently have an active session",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      case ProfileLevel.rejected:
        return {
          title: "Profile Rejected",
          message: "Your profile was not approved. Please contact support.",
          color: "text-red-500",
          imagePath: <CrossRed className="w-32 h-32 text-red-500 mx-auto" />,
          showButton: false,
        };
      
      case ProfileLevel.profileCompleted:
        return {
          title: "Profile Complete",
          message: "Your profile is complete and ready for submission",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      case ProfileLevel.approved:
        // Should not show modal for approved, but handle it just in case
        return {
          title: "",
          message: "",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      default:
        return {
          title: "Profile Incomplete",
          message: "Please complete your profile to continue",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: true,
          buttonText: "Continue",
          buttonAction: goToProfile,
        };
    }
  };

  const profileLevel = useMemo(() => checkProfileLevel(), [registeredData]);
  const config = useMemo(() => getProfileLevelConfig(profileLevel), [profileLevel]);

  // Don't show modal if approved (shouldn't happen, but safety check)
  if (profileLevel === ProfileLevel.approved) {
    return null;
  }

  // Render image based on type
  const renderImage = () => {
    if (typeof config.imagePath === 'string') {
      return (
        <img
          src={config.imagePath}
          alt={config.title}
          className="w-32 mx-auto mb-2"
        />
      );
    }
    return config.imagePath;
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
      onClick={(e) => {
        // Only close if clicking on the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white p-6 rounded-lg shadow-lg w-[85%] sm:w-[364px] text-center transform transition-transform duration-300 ease-in-out translate-x-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center">
          {renderImage()}
        </div>
        <h2 className={`text-base ${config.color} font-semibold mb-4`}>
          {config.title}
        </h2>
        <p className="text-labelTextColor text-sm mb-4">
          {config.message}
        </p>
        {config.showButton && config.buttonText && config.buttonAction && (
          <button
            onClick={config.buttonAction}
            className="bg-primaryBlueText text-white px-4 py-2 rounded-full"
          >
            {config.buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
